import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Copy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { shareViaWhatsApp, WhatsAppIcon } from '../../lib/whatsapp';

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const buildPitch = ({ venueName, lowestPrice, contactName, previewUrl, hookStyle }) => {
  const name = contactName ? `Hi ${contactName},` : 'Hi there,';
  const hooks = {
    professional: `Looking for a *premium Bangalore business address* with GST registration support? Clayworks has you covered.`,
    casual: `Quick one — got a moment? Setting up GST for your business? You might want to see this.`,
    direct: `If you need a Bangalore business address + GST registration support, I have a plan starting at *${fmtINR(lowestPrice)}/month*.`,
    benefit: `Save the hassle of leasing an office — get a *premium Bangalore address*, GST docs, mail handling and meeting room perks all-in-one.`,
  };
  return `${name}

${hooks[hookStyle] || hooks.professional}

🏢 *Clayworks Virtual Office at ${venueName}*

✅ Premium business address in Bangalore
✅ GST registration documentation support
✅ Mail & courier handling
✅ Visitor acknowledgment
✅ Preferential rates on meeting rooms & day passes

💰 Plans from *${fmtINR(lowestPrice)}/month* (billed annually, 12-month commitment, excl. GST)

See all plans + comparison here:
${previewUrl}

Happy to walk you through the options. Reply or call me anytime!

— Clayworks Team`;
};

const HOOK_OPTIONS = [
  { id: 'professional', label: 'Professional · Default' },
  { id: 'casual', label: 'Casual · Conversational' },
  { id: 'direct', label: 'Direct · Price-led' },
  { id: 'benefit', label: 'Benefit · Value-led' },
];

const WhatsAppPitchModal = ({ open, onClose, api, venue, user }) => {
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [hookStyle, setHookStyle] = useState('professional');
  const [lowestPrice, setLowestPrice] = useState(null);
  const [pitch, setPitch] = useState('');
  const [loading, setLoading] = useState(true);

  const previewUrl = venue ? `${window.location.origin}/preview/virtual-office/${venue.id}${user?.id ? `?ref=${user.id}` : ''}` : '';

  useEffect(() => {
    if (!open || !venue) return;
    setContactName(''); setContactPhone(''); setHookStyle('professional');
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/public/virtual-office/${venue.id}`);
        const plans = (res.data?.plans || []).filter((p) => p.monthly_price > 0);
        const lowest = plans.length ? Math.min(...plans.map((p) => p.monthly_price)) : 0;
        setLowestPrice(lowest);
      } catch (e) {
        toast.error('Failed to load pricing');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, venue, api]);

  useEffect(() => {
    if (loading || !venue) return;
    setPitch(buildPitch({
      venueName: venue.name,
      lowestPrice: lowestPrice || 0,
      contactName,
      previewUrl,
      hookStyle,
    }));
  }, [contactName, hookStyle, lowestPrice, loading, venue, previewUrl]);

  const copy = () => {
    navigator.clipboard.writeText(pitch);
    toast.success('Pitch copied');
  };

  const shareWA = () => shareViaWhatsApp(pitch, contactPhone);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="vo-pitch-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Generate WhatsApp Pitch</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prospect Name (optional)</Label>
                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Personalizes the greeting" data-testid="vo-pitch-name" />
              </div>
              <div>
                <Label>WhatsApp Number (optional)</Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="9876543210" data-testid="vo-pitch-phone" />
              </div>
            </div>
            <div>
              <Label>Pitch Style</Label>
              <Select value={hookStyle} onValueChange={setHookStyle}>
                <SelectTrigger data-testid="vo-pitch-style"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HOOK_OPTIONS.map((h) => <SelectItem key={h.id} value={h.id}>{h.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preview · Edit if needed</Label>
              <Textarea
                value={pitch}
                onChange={(e) => setPitch(e.target.value)}
                rows={14}
                className="font-mono text-xs leading-relaxed"
                data-testid="vo-pitch-preview"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              📍 {venue?.name} · Starting at <strong>{fmtINR(lowestPrice)}/month</strong>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copy} data-testid="vo-pitch-copy"><Copy className="w-4 h-4 mr-2" /> Copy</Button>
          <Button onClick={shareWA} className="bg-green-600 hover:bg-green-700 text-white" data-testid="vo-pitch-whatsapp">
            <WhatsAppIcon className="w-4 h-4 mr-2" /> Open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppPitchModal;
